'use client';

import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next-intl/link';
import { useLocaleRoutes } from '../utils/useLocaleRoutes';
import {
  KrsNr,
  NipNr,
  OrgNr,
  RegonNr,
  VatNr,
  YtunnusNr,
} from './ContactColumn';
import getOption from '../utils/getOption';
import { getPageData } from '../utils/getPageData';

const fixedPriceUrls = [
  'https://ATSNorway.no',
  'https://ATSSweden.se',
  'https://ATSPolska.pl',
  'https://ATSFinland.fi',
  'https://Landbrukssalg.no',
  'https://Lantbrukssalj.se',
];

const auctionUrls = ['https://ATSAuksjon.no', 'https://ATSAuktion.se'];

function getPageTitle(slug: string, locale: string) {
  const page = getPageData(slug) as any;
  const title =
    page['meta-title-' + locale] ||
    page['title-' + locale] ||
    page.title ||
    page['name-' + locale] ||
    page.name ||
    slug;
  return title;
}

export default function Footer() {
  const locale = useLocale();
  const t = useTranslations('navigation');
  const f = useTranslations('Footer');
  const n = useTranslations('Navbar');
  const path = useLocaleRoutes();

  return (
    <>
      <div className='bg-black'>
        <footer className='relative z-20 pb-4 pt-14 md:pt-28'>
          <div className='mx-auto max-w-[90rem] items-center px-4 md:px-10'>
            <div>
              <a href='/' className='mb-8'>
                <Image
                  src='/images/ats-group2.svg'
                  alt='ATS Group'
                  width={0}
                  height={0}
                  priority
                  style={{ width: '200px', height: 'auto' }}
                />
              </a>
            </div>
            <div className='my-2 flex items-center gap-4 text-lg uppercase'>
              Norway
              <span className='inline-block h-[1em] w-px border-l border-brand'></span>
              Sweden
              <span className='inline-block h-[1em] w-px border-l border-brand'></span>
              Poland
              <span className='inline-block h-[1em] w-px border-l border-brand'></span>
              Finland
            </div>
            <div className='mb-8 mt-4 flex flex-col justify-between gap-10 lg:flex-row'>
              <div>
                <p className='mb-12 max-w-[24em]'>
                  {/*f.rich('description', {
                    b: (chunks) => (
                      <b className='font-bold text-brand'>{chunks}</b>
                    ),
                  })*/}
                  <span className='left-text'>
                    ATSGroup consist of specialist brokers of used{' '}
                    <span className='text-brand'>
                      {' '}
                      construction machinery, trucks, buses{' '}
                    </span>
                    and <span className='text-brand'>other equipment</span> for
                    the <span className='text-brand'>transport</span> and{' '}
                    <span className='text-brand'>construction industry</span>{' '}
                    for a global market. We have offices in Norway, Sweden,
                    Poland and Finland.{' '}
                  </span>
                </p>
                <h4 className='font-bold'>{f('contactHeader')}</h4>
                <div className='mt-2 flex max-w-[30em] flex-wrap justify-between gap-4 border-t border-accent pt-2 text-sm'>
                  <div
                    className='mb-6'
                    dangerouslySetInnerHTML={{
                      __html: String(
                        process.env.NEXT_PUBLIC_CONTACT
                      ).replaceAll('|', '<br>'),
                    }}
                  />
                  <div className='flex flex-col'>
                    {!!OrgNr() ? (
                      <div>
                        <OrgNr />
                      </div>
                    ) : null}
                    {!!VatNr() ? (
                      <div>
                        <VatNr />
                      </div>
                    ) : null}
                    {!!KrsNr() ? (
                      <div>
                        <KrsNr />
                      </div>
                    ) : null}
                    {!!NipNr() ? (
                      <div>
                        <NipNr />
                      </div>
                    ) : null}
                    {!!RegonNr() ? (
                      <div>
                        <RegonNr />
                      </div>
                    ) : null}
                    {!!YtunnusNr() ? (
                      <div>
                        <YtunnusNr />
                      </div>
                    ) : null}
                    {process.env.NEXT_PUBLIC_MAIL && (
                      <div>
                        <Link
                          href={`mailto:${String(process.env.NEXT_PUBLIC_MAIL).replaceAll(' ', '')}`}
                          className='underline underline decoration-brand underline-offset-[.25em] transition duration-200 hover:text-brand'
                        >
                          {String(process.env.NEXT_PUBLIC_MAIL)}
                        </Link>
                      </div>
                    )}
                    {process.env.NEXT_PUBLIC_PHONE && (
                      <div>
                        <Link
                          href={`tel:${String(process.env.NEXT_PUBLIC_PHONE).replaceAll(' ', '')}`}
                          className='underline underline decoration-brand underline-offset-[.25em] transition duration-200 hover:text-brand'
                        >
                          {String(process.env.NEXT_PUBLIC_PHONE)}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <ul className='flex flex-col gap-y-2 border-l border-accent pl-8 lg:min-h-[20em]'>
                  <li>
                    <Link
                      href={`/${path('about-ats')}`}
                      className='block transition duration-200 hover:text-brand focus:text-brand'
                    >
                      {t('about')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/${path('contact')}`}
                      className='block transition duration-200 hover:text-brand focus:text-brand'
                    >
                      {t('contact')}
                    </Link>
                  </li>
                  {/*<li>
                    <Link
                      href={`/${path('sustainability')}`}
                      className='block transition duration-200 hover:text-brand focus:text-brand'
                    >
                      Sustainability
                    </Link>
                  </li>*/}
                  <li>
                    <Link
                      href={`/${path('privacy-policy')}`}
                      className='block transition duration-200 hover:text-brand focus:text-brand'
                    >
                      {t('privacy')}
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className='mb-4 text-2xl text-accent-light'>
                  {f('atsLinkHeader')}
                </h3>
                <div className='flex flex-col gap-10 md:flex-row'>
                  <div>
                    <Image
                      src='/images/fixed-price-icon.svg'
                      alt='ATS'
                      width={0}
                      height={0}
                      priority
                      style={{ width: '100px', height: 'auto' }}
                    />
                    <h4 className='my-4 uppercase text-brand'>
                      {f('fixedPriceHeader')}
                    </h4>
                    <ul
                      className='my-4 max-w-[20em] border-b border-accent lg:min-w-[14em]'
                      translate='no'
                    >
                      {fixedPriceUrls.map((url) => (
                        <li key={url}>
                          <Link
                            href={url.toLocaleLowerCase()}
                            target='_blank'
                            className='group flex items-center justify-between border-t border-accent py-1 transition duration-200 hover:text-brand focus:text-brand'
                          >
                            {url.replace('https://', '')}
                            <svg
                              width='15'
                              height='16'
                              viewBox='0 0 15 16'
                              xmlns='http://www.w3.org/2000/svg'
                              className='text-accent transition duration-200 group-hover:text-accent-light group-focus:text-accent-light'
                            >
                              <path
                                d='M5.875 3.50806H2.625C1.72754 3.50806 1 4.23559 1 5.13306V13.2581C1 14.1555 1.72754 14.8831 2.625 14.8831H10.75C11.6475 14.8831 12.375 14.1555 12.375 13.2581V10.0081M9.125 1.88306H14M14 1.88306V6.75806M14 1.88306L5.875 10.0081'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <Image
                      src='/images/auction-icon.svg'
                      alt='ATS'
                      width={0}
                      height={0}
                      priority
                      style={{ width: '100px', height: 'auto' }}
                    />
                    <h4 className='my-4 uppercase text-brand'>
                      {f('auctionHeader')}
                    </h4>
                    <ul
                      className='my-4 max-w-[20em] border-b border-accent lg:min-w-[14em]'
                      translate='no'
                    >
                      {auctionUrls.map((url) => (
                        <li key={url}>
                          <Link
                            href={url.toLocaleLowerCase()}
                            target='_blank'
                            className='group flex items-center justify-between border-t border-accent py-1 transition duration-200 hover:text-brand focus:text-brand'
                          >
                            {url.replace('https://', '')}
                            <svg
                              width='15'
                              height='16'
                              viewBox='0 0 15 16'
                              xmlns='http://www.w3.org/2000/svg'
                              className='text-accent transition duration-200 group-hover:text-accent-light group-focus:text-accent-light'
                            >
                              <path
                                d='M5.875 3.50806H2.625C1.72754 3.50806 1 4.23559 1 5.13306V13.2581C1 14.1555 1.72754 14.8831 2.625 14.8831H10.75C11.6475 14.8831 12.375 14.1555 12.375 13.2581V10.0081M9.125 1.88306H14M14 1.88306V6.75806M14 1.88306L5.875 10.0081'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <div className='bg-[#00C2A44D] py-4 text-right'>
        <div className='mx-auto max-w-[90rem] items-center px-4 md:px-10'>
          © {new Date().getFullYear()} {getOption('TITLE')}
        </div>
      </div>
    </>
  );
}
